import { Button, Grid, Input, Modal, Select, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { UndoOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useState } from 'react';
import { ApplicationsFilter } from '../../../../shared/VolunteerApplicationServiceTypes';
import { convertStringToInt } from '../../../../shared/Utils';
import RegionsDropdown from '../../../../components/RegionsDropdown';
import ZoneDropdown from '../../../../components/ZonesDropdown';
import { useAuth } from '../../../../hooks/useAuth';
import { ApplicationStatus } from '../../../../shared/Constants';
import { PreworkStatus } from '../../../../shared/PreWorkServiceTypes';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface FilterProps {
	open: boolean;
	onClose: () => void;
	eligibleCandidateValue: ApplicationsFilter;
	onEligibleCandidateFilterChange: (value: ApplicationsFilter) => void;
	batchIds: Array<number>;
	resetTrigger?: number;
}

interface OptionData {
	value: number | undefined;
	label: string | undefined;
}

interface ZoneOption {
	value: string;
	label: string;
}

export const FilterEligibleCandidates = ({
	open,
	onClose,
	eligibleCandidateValue,
	onEligibleCandidateFilterChange,
	batchIds,
	resetTrigger,
}: FilterProps) => {
	const { t, i18n } = useTranslation();
	const lengthyLanguages = ['ta', 'te', 'ka', 'ma'];
	const isLengthyLanguage = lengthyLanguages.includes(i18n.language);
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const isStackVertical = isLengthyLanguage && isMobile;

	const { srcmId } = useAuth();

	const [searchBySrcmId, setSearchBySrcmId] = useState<string | undefined>();
	const [candidateName, setCandidateName] = useState<string | undefined>();
	const [status, setStatus] = useState<string[] | undefined>();
	const [preworkStatusUI, setPreworkStatusUI] = useState<
		Array<PreworkStatus> | undefined
	>();
	const [zoneNames, setZoneNames] = useState<Array<string> | undefined>();
	const [selectedZones, setSelectedZones] = useState<ZoneOption[]>([]);
	const [regions, setRegions] = useState<OptionData>();

	const resetFields = useCallback(() => {
		setSearchBySrcmId(undefined);
		setCandidateName(undefined);
		setStatus(undefined);
		setPreworkStatusUI(undefined);
		setZoneNames(undefined);
		setSelectedZones([]);
		setRegions(undefined);
	}, []);

	useEffect(() => {
		if (resetTrigger !== undefined) {
			resetFields();
		}
	}, [resetFields, resetTrigger]);

	const handleZoneChange = useCallback(
		(value: Array<{ value: string; label: string }>) => {
			const arr = Array.isArray(value) ? value : value ? [value] : [];

			const zoneName = arr.map((z) => String(z.label));

			setZoneNames(zoneName);
			setSelectedZones(arr);
		},
		[]
	);

	const handleRegionsChange = useCallback((value: OptionData) => {
		setRegions(value);
	}, []);

	const handleResetFilters = useCallback(() => {
		resetFields();
		onEligibleCandidateFilterChange({ batchIds });
		onClose();
		setSelectedZones([]);
		setZoneNames(undefined);
	}, [resetFields, onEligibleCandidateFilterChange, batchIds, onClose]);

	const handleFilterButtonClick = useCallback(() => {
		const regionIntValue = convertStringToInt(regions?.value);
		let preworkStatusValue = preworkStatusUI ? [...preworkStatusUI] : [];
		if (preworkStatusValue.includes(PreworkStatus.PREWORK_PENDING)) {
			preworkStatusValue = [
				...preworkStatusValue,
				PreworkStatus.PREWORK_REJECTED,
			];
		}
		const defaultFilters: ApplicationsFilter = {
			...(regionIntValue && { regionIds: [regionIntValue] }),
			searchByApplicantName: candidateName,
			applicantSrcmId: searchBySrcmId,
			status,
			zoneNames,
			batchIds,
			preworkStatus: preworkStatusValue,
		};
		onEligibleCandidateFilterChange(defaultFilters);
		onClose();
	}, [
		regions?.value,
		preworkStatusUI,
		candidateName,
		searchBySrcmId,
		status,
		zoneNames,
		batchIds,
		onEligibleCandidateFilterChange,
		onClose,
	]);

	useEffect(() => {
		if (eligibleCandidateValue) {
			setSearchBySrcmId(eligibleCandidateValue.applicantSrcmId);
			setCandidateName(eligibleCandidateValue.searchByApplicantName);
			setStatus(eligibleCandidateValue.status);

			const uiValues = eligibleCandidateValue.preworkStatus?.filter(
				(preworkStatus) => preworkStatus !== PreworkStatus.PREWORK_REJECTED
			);
			setPreworkStatusUI(uiValues);
		}
	}, [eligibleCandidateValue]);

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			centered
			className="min-w-[800px]"
			closable
			maskClosable
		>
			<div className="text-center font-sans font-bold text-lg border-b-[2px] border-gray-700 pb-4">
				{t('filterBatch')}
			</div>

			<div className="flex flex-col sm:flex-row flex-wrap gap-x-4">
				{/* Batch Description */}
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="block text-xs text-gray-650">
							{t('searchBySRCMId')}
						</Text>
						<Input
							className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
							value={searchBySrcmId}
							allowClear
							onChange={(e) => setSearchBySrcmId(e.target.value)}
						/>
					</div>
				</div>

				{/* Candidate Name */}
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="text-xs text-gray-650">{t('candidateName')}</Text>
						<Input
							className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
							value={candidateName}
							allowClear
							onChange={(e) => setCandidateName(e.target.value)}
						/>
					</div>
				</div>

				{/* Status */}
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="text-xs text-gray-650">
							{t('applicationStatus')}
						</Text>
						<Select
							defaultActiveFirstOption={false}
							filterOption={false}
							mode="multiple"
							onChange={(value) => setStatus(value)}
							value={status}
							options={[
								{
									value: ApplicationStatus.USER_SUBMITTED_APPLICATION,
									label: t('proposerNotAssigned'),
								},
								{
									value: ApplicationStatus.FUNCTIONARY_ASSIGNED,
									label: t('waitingForProposerReview'),
								},
								{
									value: ApplicationStatus.FUNCTIONARY_SUBMITTED,
									label: t('proposerProvidedRecommendation'),
								},
								{
									value: ApplicationStatus.WAITING_FOR_CARE_TEAM,
									label: t('waitingForCareTeamReview'),
								},
								{
									value: ApplicationStatus.APPROVED,
									label: t('screeningApproved'),
								},
								{
									value: ApplicationStatus.REJECTED,
									label: t('rejected'),
								},
							]}
							className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
						/>
					</div>
				</div>
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="text-xs text-gray-650">{t('preworkStatus')}</Text>
						<Select
							defaultActiveFirstOption={false}
							filterOption={false}
							mode="multiple"
							onChange={(value) => setPreworkStatusUI(value)}
							value={preworkStatusUI}
							options={[
								{
									value: PreworkStatus.PREWORK_PENDING,
									label: t('pending'),
								},
								{
									value: PreworkStatus.PREWORK_APPROVED,
									label: t('approved'),
								},
							]}
							className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
						/>
					</div>
				</div>
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="block text-xs text-gray-650">
							{t('filterByRegion')}
						</Text>
						<RegionsDropdown
							functionarySrcmId={srcmId}
							value={regions}
							onChange={handleRegionsChange}
						/>
					</div>
				</div>

				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="block text-xs text-gray-650">
							{t('filterByZone')}
						</Text>
						<ZoneDropdown
							functionarySrcmId={srcmId}
							value={selectedZones}
							onChange={handleZoneChange}
							regionId={convertStringToInt(regions?.value)}
							key={regions?.value}
							disabled={!regions?.value}
							multiple={true}
						/>
					</div>
				</div>
			</div>

			<div className="flex flex-col items-center space-y-2 gap-3 md:flex-row md:justify-center md:gap-2 mt-4 ant-modal-footer">
				<Button
					className="w-full md:flex-1 h-9 text-primary-blue font-sans font-bold"
					type="link"
					onClick={handleResetFilters}
				>
					<span>
						{t('resetFilters')}
						<UndoOutlined className="ml-2" />
					</span>
				</Button>
				<Button
					className={`w-full md:flex-1 h-9 text-primary-blue font-sans font-bold ${
						isStackVertical ? 'p-2' : ''
					}`}
					onClick={onClose}
				>
					{t('closeFilter')}
				</Button>
				<Button
					type="primary"
					onClick={handleFilterButtonClick}
					className={`w-full md:flex-1 h-9 font-sans font-bold ${
						isStackVertical ? 'p-2 mt-3' : ''
					}`}
				>
					{t('applyFilter')}
				</Button>
			</div>
		</Modal>
	);
};
